import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { User, Users, Plus, Trash2, Eye, EyeOff, Save, ArrowLeft } from 'lucide-react';

const emptyOwner = {
  first_name: '',
  middle_name: '',
  last_name: '',
  title: '',
  ownership_percent: '',
  dob: '',
  ssn: '',
  home_address: '',
  mobile_number: '',
  email: ''
};

function Step2OwnerInfo({ application, onNext, onPrevious, saving, onSaveDraft }) {
  const section = application.sections?.find(s => s.step_number === 2) || {};
  const existingOwners = section.data?.owners || application.owners;
  const [owners, setOwners] = useState(
    existingOwners?.length > 0 ? existingOwners : [{ ...emptyOwner }]
  );
  const [showSSN, setShowSSN] = useState({});

  const updateOwner = (index, field, value) => {
    const updated = [...owners];
    updated[index] = { ...updated[index], [field]: value };
    setOwners(updated);
  };

  const addOwner = () => setOwners([...owners, { ...emptyOwner }]);

  const removeOwner = (index) => {
    if (owners.length <= 1) return;
    setOwners(owners.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext({ owners });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Principal Owner */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#3b82f6] flex items-center justify-center shrink-0">
          <User className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold" data-testid="step2-title">Principal Owner Information</h3>
          <p className="text-sm text-muted-foreground">Please provide details for the primary business owner</p>
        </div>
      </div>

      {owners.map((owner, index) => (
        <div key={index} className="space-y-5" data-testid={`owner-form-${index}`}>
          {index > 0 && (
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-[#3b82f6]" />
                <span className="font-medium text-sm">Additional Owner {index}</span>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => removeOwner(index)} data-testid={`remove-owner-${index}`}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          )}

          {/* Full Legal Name - 3 col */}
          <div>
            <Label className="mb-1.5 block">Full Legal Name *</Label>
            <div className="grid gap-3 md:grid-cols-3">
              <Input
                value={owner.first_name}
                onChange={(e) => updateOwner(index, 'first_name', e.target.value)}
                placeholder="First Name"
                required
                data-testid={`owner-${index}-first-name`}
              />
              <Input
                value={owner.middle_name}
                onChange={(e) => updateOwner(index, 'middle_name', e.target.value)}
                placeholder="Middle Name"
                data-testid={`owner-${index}-middle-name`}
              />
              <Input
                value={owner.last_name}
                onChange={(e) => updateOwner(index, 'last_name', e.target.value)}
                placeholder="Last Name"
                required
                data-testid={`owner-${index}-last-name`}
              />
            </div>
          </div>

          {/* Title + Ownership % */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="mb-1.5 block">Title *</Label>
              <Select value={owner.title} onValueChange={(v) => updateOwner(index, 'title', v)}>
                <SelectTrigger data-testid={`owner-${index}-title`}>
                  <SelectValue placeholder="Select title" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Owner">Owner</SelectItem>
                  <SelectItem value="CEO">CEO</SelectItem>
                  <SelectItem value="President">President</SelectItem>
                  <SelectItem value="CFO">CFO</SelectItem>
                  <SelectItem value="Vice President">Vice President</SelectItem>
                  <SelectItem value="Partner">Partner</SelectItem>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Managing Partner">Managing Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Ownership Percentage *</Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={owner.ownership_percent}
                  onChange={(e) => updateOwner(index, 'ownership_percent', e.target.value)}
                  placeholder="0"
                  required
                  className="pr-8"
                  data-testid={`owner-${index}-ownership`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
              </div>
            </div>
          </div>

          {/* DOB + SSN */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="mb-1.5 block">Date of Birth *</Label>
              <Input
                type="date"
                value={owner.dob}
                onChange={(e) => updateOwner(index, 'dob', e.target.value)}
                required
                data-testid={`owner-${index}-dob`}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Social Security Number *</Label>
              <div className="relative">
                <Input
                  type={showSSN[index] ? 'text' : 'password'}
                  value={owner.ssn}
                  onChange={(e) => updateOwner(index, 'ssn', e.target.value)}
                  placeholder="XXX-XX-XXXX"
                  required
                  className="pr-10"
                  data-testid={`owner-${index}-ssn`}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowSSN(prev => ({ ...prev, [index]: !prev[index] }))}
                >
                  {showSSN[index] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Home Address */}
          <div>
            <Label className="mb-1.5 block">Home Address *</Label>
            <Input
              value={owner.home_address}
              onChange={(e) => updateOwner(index, 'home_address', e.target.value)}
              placeholder="Start typing address..."
              required
              data-testid={`owner-${index}-address`}
            />
          </div>

          {/* Mobile + Email */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label className="mb-1.5 block">Mobile Number *</Label>
              <Input
                value={owner.mobile_number}
                onChange={(e) => updateOwner(index, 'mobile_number', e.target.value)}
                placeholder="(555) 123-4567"
                required
                data-testid={`owner-${index}-mobile`}
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Email Address *</Label>
              <Input
                type="email"
                value={owner.email}
                onChange={(e) => updateOwner(index, 'email', e.target.value)}
                placeholder="owner@example.com"
                required
                data-testid={`owner-${index}-email`}
              />
            </div>
          </div>
        </div>
      ))}

      {/* Add Another Owner */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center">
            <Users className="h-4 w-4 text-[#3b82f6]" />
          </div>
          <div>
            <div className="font-medium text-sm">Additional Owners</div>
            <div className="text-xs text-muted-foreground">Add any additional owners with 25% or more ownership</div>
          </div>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addOwner} data-testid="add-owner-btn">
          <Plus className="h-4 w-4 mr-1" />
          Add Another Owner
        </Button>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button type="button" variant="outline" onClick={() => onSaveDraft?.({ owners })} data-testid="save-draft-btn">
          <Save className="h-4 w-4 mr-2" />
          Exit & Save Draft
        </Button>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={onPrevious} data-testid="previous-step-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button type="submit" disabled={saving} className="bg-[#3b82f6] hover:bg-[#2563eb]" data-testid="save-continue-btn">
            {saving ? 'Saving...' : 'Save & Continue \u2192'}
          </Button>
        </div>
      </div>
    </form>
  );
}

export default Step2OwnerInfo;
